/**
 * Selected-question detail pane: question header and stacked sub-criteria cards.
 */

import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo, useState } from "react";
import type {
  AtomScreeningAnswer,
  ScreeningAnswer,
} from "../../../../api/clinicalTrialsApiTypes";
import type { ScreeningOverride } from "../../../../data-model/eligibilityDetail";
import CriteriaCard from "../../shared/CriteriaCard";
import { ManualOverrideModal } from "../../shared/ManualOverrideModal";
import { ViewEvidenceModal } from "../../shared/ViewEvidenceModal";
import {
  canViewAtomEvidence,
  collectEvidenceSourcePaths,
  type ViewEvidenceContext,
} from "../../shared/viewEvidenceTypes";
import {
  questionDisplayText,
  screeningStatusColors,
  screeningStatusLabel,
} from "../../shared/screeningStatusDisplay";
import {
  getStackedBorders,
  getStackedRoundedCorners,
} from "../../shared/stackedCardChrome";

/** Props for the question header and atom criteria stack. */
export interface QuestionAndCriteriaProps {
  question: ScreeningAnswer;
  questionIndex: number;
  overrides: ScreeningOverride[];
}

/**
 * Criterion statement copy for one atom.
 *
 * The card statement is the protocol atom sentence. The deciding fact is the finding,
 * shown on its own row, so the two lines do not repeat each other.
 *
 * @param atom - Atom screening answer
 * @returns Protocol atom text, or the atom id when the protocol has none
 */
function atomStatementText(atom: AtomScreeningAnswer): string {
  if (atom.atom_text?.trim()) {
    return atom.atom_text.trim();
  }
  return atom.atom_id;
}

/**
 * Primary finding copy for one atom.
 *
 * @param decidingFact - Agent deciding fact from screening
 * @returns Finding body text for the criteria card
 */
function atomFindingText(decidingFact: string | null | undefined): string {
  if (decidingFact?.trim()) {
    return decidingFact.trim();
  }
  return "No deciding fact was returned.";
}

/**
 * Render the question at the top and protocol atoms as stacked criteria cards.
 *
 * @param props - Selected question, its list index, and durable overrides
 * @returns Question header plus sub-criteria cards
 */
const QuestionAndCriteria = ({
  question,
  questionIndex,
  overrides,
}: QuestionAndCriteriaProps) => {
  const [overrideAtomId, setOverrideAtomId] = useState<string | null>(null);
  const [evidenceContext, setEvidenceContext] =
    useState<ViewEvidenceContext | null>(null);

  const status = useMemo(() => {
    if (question.status === "DATA_MISSING") {
      return "Action Pending";
    }
    return question.status || question.answer || "NOT_EVALUATED";
  }, [question.status, question.answer]);

  const statusColors = screeningStatusColors(status);
  const atoms = question.atoms ?? [];
  const atomCount = atoms.length;
  // const reasoning = question.reasoning?.trim() ?? "";
  const questionText =
    questionDisplayText(question) || "No question text was returned.";

  const activeOverride = overrides.find(
    (item) => item.atom_id === overrideAtomId,
  );
  const activeAtom = atoms.find((atom) => atom.atom_id === overrideAtomId);

  return (
    <div className="evaluation-questions-and-criteria--container flex h-full flex-col gap-4 overflow-y-auto bg-white">
      <ManualOverrideModal
        isOpen={overrideAtomId !== null}
        atomId={overrideAtomId ?? ""}
        currentAtomStatus={activeAtom?.status ?? ""}
        existingOverride={activeOverride}
        onClose={() => setOverrideAtomId(null)}
      />
      <ViewEvidenceModal
        isOpen={evidenceContext !== null}
        context={evidenceContext}
        onClose={() => setEvidenceContext(null)}
      />
      <div className="evaluation-questions flex flex-col gap-[0.38rem] border-b border-primaryGray-14 pb-4">
        <div className="question-number text-overline font-semibold uppercase tracking-[0.0625rem] text-primaryGray-7">
          Question {questionIndex}
        </div>
        <div className="question-details-with-status flex items-start gap-5">
          <div className="question-details flex-1 text-small font-normal text-primaryGray-1">
          {/* {reasoning && reasoning !== questionText ? (
              // <div className="mt-2">{reasoning}</div>
            ) : null} */}
            <div className="mt-2 text-primaryGray-4">{questionText}</div>
           
          </div>
          <div className="question-status shrink-0">
            <Tags label={screeningStatusLabel(status)} {...statusColors} />
          </div>
        </div>
      </div>
      {atomCount > 0 && (
        <div className="evaluation-criteria flex-1">
          <div className="sub-criteria--section mb-4 flex items-center gap-2">
            <span className="text-overline font-semibold uppercase tracking-[0.0625rem] text-primaryGray-8">
              Sub Criteria
            </span>
            <span className="count flex h-4 w-4 items-center justify-center rounded-full bg-primaryGray-15 text-overline font-semibold text-primaryGray-5">
              {atomCount}
            </span>
          </div>
          {atoms.map((atom, index) => {
            const override = overrides.find(
              (item) => item.atom_id === atom.atom_id,
            );
            const sourcePaths = collectEvidenceSourcePaths([
              atom.source_path,
              question.source_path,
              override?.justification_source_path,
            ]);
            return (
              <CriteriaCard
                key={atom.atom_id}
                id={atom.atom_id}
                indexLabel={`${index + 1}/${atomCount}`}
                statement={atomStatementText(atom)}
                finding={atomFindingText(atom.deciding_fact)}
                overrideComment={override?.comment}
                screeningStatus={atom.status}
                hasEvidence={canViewAtomEvidence(
                  sourcePaths,
                  atom.source,
                  atom.deciding_fact,
                )}
                overrideVerdict={override?.verdict ?? null}
                onManualOverrideClick={() => setOverrideAtomId(atom.atom_id)}
                onViewEvidenceClick={() =>
                  setEvidenceContext({
                    criteriaTitle: `Criteria ${questionIndex}`,
                    primaryCriteria: questionText,
                    subCriteria: atomStatementText(atom),
                    patientFinding: atomFindingText(atom.deciding_fact),
                    sourcePaths,
                    textEvidenceSource: atom.source,
                    textEvidenceDecidingFact: atom.deciding_fact,
                  })
                }
                roundedCorners={getStackedRoundedCorners(index, atomCount)}
                borders={getStackedBorders(index, atomCount)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default QuestionAndCriteria;
